import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { DarkTheme, NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useSampleData } from '../../contexts/SampleDataContext';
import { ExploreScreen } from '../../modules/Landing/ExploreScreen';
import { LandingScreen } from '../../modules/Landing/LandingScreen';
import { LegalScreen } from '../../modules/Landing/LegalScreen';
import { DeleteAccountScreen } from '../../modules/Landing/DeleteAccountScreen';
import { ThankYouScreen } from '../../modules/Landing/ThankYouScreen';
import { ImportOnboardingModal } from '../../modules/ImportOnboarding';
import { MainTabNavigator } from '../../modules/Main/navigation/MainTabNavigator';
import { OnboardingModal } from '../../modules/Onboarding/OnboardingModal';
import { SettingsScreen } from '../../modules/Settings/SettingsScreen';
import { ProWelcomeModal } from '../../components/billing/ProWelcomeModal';
import { BootSplash } from './BootSplash';
import { AuthOverlayHost } from './AuthOverlayHost';
import { webLinking } from './linking.web';
import { navigationRef, openWebAppSignIn } from './navigationRef';
import type { RootStackParamList } from './types';
import { WebDocumentMeta } from './WebDocumentMeta.web';
import { colors } from '../theme/tokens';
import { isDeleteAccountPath, isMarketingPath, WEB_DELETE_ACCOUNT_LEGACY_PATH, WEB_DELETE_ACCOUNT_PATH, WEB_EXPLORE_PATH, WEB_PRIVACY_PATH, WEB_SETTINGS_PATH, WEB_SUPPORT_PATH, WEB_TERMS_PATH, WEB_THANKS_PATH } from './webPaths';
import { publishWebPathname } from './webRouteStore';
import { trackNavigationState } from '../../services/navigationTelemetry';
import type { TelemetryTab } from '../../services/telemetryTypes';

const Stack = createNativeStackNavigator<RootStackParamList>();

const webNavTheme = {
  ...DarkTheme,
  colors: {
    ...DarkTheme.colors,
    background: colors.background,
    card: colors.background,
    primary: colors.accent,
    text: colors.text,
    border: colors.glassBorder,
  },
};

function PrivacyScreen() {
  return <LegalScreen kind="privacy" />;
}

function TermsScreen() {
  return <LegalScreen kind="terms" />;
}

function SupportScreen() {
  return <LegalScreen kind="support" />;
}

function initialWebRoute(): keyof RootStackParamList {
  if (typeof window === 'undefined') return 'Landing';
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === WEB_PRIVACY_PATH) return 'Privacy';
  if (path === WEB_TERMS_PATH) return 'Terms';
  if (path === WEB_SUPPORT_PATH) return 'Support';
  if (path === WEB_THANKS_PATH) return 'Thanks';
  if (isDeleteAccountPath(path)) return 'DeleteAccount';
  if (path === WEB_SETTINGS_PATH) return 'Settings';
  if (path === WEB_EXPLORE_PATH || path.startsWith(`${WEB_EXPLORE_PATH}/`)) return 'Explore';
  if (isMarketingPath(path)) return 'Landing';
  return 'MainTabs';
}

export function RootNavigator() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isHydrated: sampleHydrated, exitSampleMode } = useSampleData();
  const { isHydrated: onboardingHydrated } = useOnboarding();
  const authedRef = React.useRef(isAuthenticated);
  authedRef.current = isAuthenticated;
  const lastRouteRef = React.useRef('');
  const lastTabRef = React.useRef<TelemetryTab | undefined>(undefined);

  useEffect(() => {
    if (isAuthenticated) {
      exitSampleMode();
    }
  }, [isAuthenticated, exitSampleMode]);

  const bounceGuestFromApp = (state?: { routes: { name?: string }[]; index: number } | null) => {
    if (!state) return;
    const name = state.routes[state.index]?.name;
    if (
      typeof window !== 'undefined' &&
      isDeleteAccountPath(window.location.pathname) &&
      name !== 'DeleteAccount'
    ) {
      navigationRef.navigate('DeleteAccount');
      return;
    }
    if (authedRef.current) return;
    if (name === 'MainTabs') {
      navigationRef.navigate('Landing');
      openWebAppSignIn();
    }
  };

  if (!sampleHydrated || !onboardingHydrated || authLoading) {
    return <BootSplash />;
  }

  return (
    <View style={styles.root}>
      <WebDocumentMeta />
      <NavigationContainer
        ref={navigationRef}
        linking={webLinking}
        theme={webNavTheme}
        onReady={() => {
          if (typeof window !== 'undefined') {
            const path = window.location.pathname.replace(/\/+$/, '') || '/';
            if (path === WEB_DELETE_ACCOUNT_LEGACY_PATH) {
              window.history.replaceState(
                window.history.state,
                '',
                `${WEB_DELETE_ACCOUNT_PATH}${window.location.search}`,
              );
            }
          }
          publishWebPathname();
          bounceGuestFromApp(navigationRef.getRootState());
          trackNavigationState(navigationRef.getRootState(), lastRouteRef, lastTabRef);
        }}
        onStateChange={(state) => {
          publishWebPathname();
          bounceGuestFromApp(state);
          trackNavigationState(state, lastRouteRef, lastTabRef);
        }}
      >
        <Stack.Navigator
          initialRouteName={initialWebRoute()}
          screenOptions={{
            headerShown: false,
            animation: 'none',
            contentStyle: styles.screen,
          }}
        >
          <Stack.Screen name="Landing" component={LandingScreen} />
          <Stack.Screen name="Privacy" component={PrivacyScreen} />
          <Stack.Screen name="Terms" component={TermsScreen} />
          <Stack.Screen name="Support" component={SupportScreen} />
          <Stack.Screen name="Thanks" component={ThankYouScreen} />
          <Stack.Screen name="DeleteAccount" component={DeleteAccountScreen} />
          <Stack.Screen name="Settings" component={SettingsScreen} />
          <Stack.Screen name="Explore" component={ExploreScreen} />
          <Stack.Screen name="MainTabs" component={MainTabNavigator} />
        </Stack.Navigator>
      </NavigationContainer>
      <AuthOverlayHost />
      <OnboardingModal />
      <ImportOnboardingModal />
      <ProWelcomeModal />
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    minHeight: '100%',
    position: 'relative',
    backgroundColor: colors.background,
  },
  screen: {
    flex: 1,
    backgroundColor: colors.background,
  },
});
